import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, Button, Chip, CircularProgress, IconButton, List, ListItem, ListItemAvatar, ListItemText, Menu, MenuItem, Popover, Typography } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupIcon from '@mui/icons-material/Group';
import { api, apiUrl } from "../../services/api";
import { AlertConfirm, AlertInfo } from "../Alerts/Alert";
import { useChat } from "./ChatProvider";
import ChatInput from "./ChatInput";
import ChatMensagem from "./ChatMensagem";
import {
    ChatAnexo,
    chaveDia,
    corAvatar,
    iniciaisNome,
    rotuloDia,
} from "./chatTypes";

type Props = {
    onVoltar?: () => void
    onEditarGrupo?: () => void
};

export default function ChatJanela({ onVoltar, onEditarGrupo }: Props) {
    const {
        conversaAtiva,
        mensagens,
        carregandoMensagens,
        temMais,
        enviando,
        idUsuarioLogado,
        enviarMensagem,
        enviarAnexos,
        apagarMensagem,
        carregarMaisMensagens,
        sairDaConversa,
    } = useChat();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [anchorParticipantes, setAnchorParticipantes] = useState<null | HTMLElement>(null);
    const fimRef = useRef<HTMLDivElement | null>(null);
    const ultimoIdRef = useRef(0);

    // Autoscroll só quando chega mensagem nova (não ao carregar histórico antigo)
    useEffect(() => {
        if (mensagens.length === 0) return;
        const ultimo = mensagens[mensagens.length - 1].id_chat_mensagem;
        if (ultimo !== ultimoIdRef.current) {
            ultimoIdRef.current = ultimo;
            fimRef.current?.scrollIntoView({ behavior: 'auto' });
        }
    }, [mensagens]);

    const baixarAnexo = useCallback(async (anexo: ChatAnexo) => {
        try {
            const response = await api.get(`/chatanexo/${anexo.id_chat_anexo}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', anexo.nome_original);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha ao baixar o arquivo' });
        }
    }, []);

    async function handleSair() {
        setAnchorEl(null);
        if (!conversaAtiva) return;
        const ehGrupo = conversaAtiva.tipo_conversa === 'grupo';
        const r = await AlertConfirm({
            title: ehGrupo ? 'Sair do grupo?' : 'Arquivar conversa?',
            text: ehGrupo
                ? 'Você deixará de receber as mensagens deste grupo.'
                : 'A conversa sai da sua lista, mas o histórico é preservado.',
        });
        if (r.isConfirmed) await sairDaConversa(conversaAtiva.id_chat_conversa);
    }

    // Menor "lida até aqui" entre os outros participantes = até onde todos leram
    const lidaAte = useMemo(() => {
        if (!conversaAtiva) return 0;
        const outros = conversaAtiva.participantes.filter(p => Number(p.id_usuario) !== Number(idUsuarioLogado));
        if (outros.length === 0) return 0;
        return Math.min(...outros.map(p => Number(p.id_ultima_mensagem_lida || 0)));
    }, [conversaAtiva, idUsuarioLogado]);

    // Menções: só em grupo. Nomes para destacar no balão, meu nome para o destaque forte.
    const participantesMencao = useMemo(
        () => (conversaAtiva?.tipo_conversa === 'grupo' ? conversaAtiva.participantes : []),
        [conversaAtiva],
    );
    const nomesMencionaveis = useMemo(
        () => participantesMencao.map(p => p.nome_usuario),
        [participantesMencao],
    );
    const meuNome = useMemo(
        () => participantesMencao.find(p => Number(p.id_usuario) === Number(idUsuarioLogado))?.nome_usuario || "",
        [participantesMencao, idUsuarioLogado],
    );

    if (!conversaAtiva) {
        return (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Typography sx={{ fontSize: 13, color: '#9e9e9e', textAlign: 'center' }}>
                    Selecione uma conversa para começar
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: '#eceff1' }}>
            {/* Cabeçalho */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.8, bgcolor: '#fff', borderBottom: '1px solid #e0e0e0' }}>
                {onVoltar && (
                    <IconButton size="small" onClick={onVoltar}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                )}
                <Avatar
                    src={conversaAtiva.foto_conversa ? `${apiUrl}/imguploaded/${conversaAtiva.foto_conversa}` : ""}
                    sx={{
                        width: 34,
                        height: 34,
                        fontSize: 13,
                        bgcolor: conversaAtiva.tipo_conversa === 'grupo' ? '#455a64' : corAvatar(conversaAtiva.id_usuario_destino || 0),
                    }}
                >
                    {conversaAtiva.tipo_conversa === 'grupo' ? <GroupIcon fontSize="small" /> : iniciaisNome(conversaAtiva.titulo_conversa)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conversaAtiva.titulo_conversa}
                    </Typography>
                    {conversaAtiva.tipo_conversa === 'grupo' ? (
                        <Typography
                            component="span"
                            onClick={e => setAnchorParticipantes(e.currentTarget)}
                            sx={{
                                fontSize: 10.5,
                                color: '#757575',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.3,
                                '&:hover': { color: '#455a64', textDecoration: 'underline' },
                            }}
                        >
                            <GroupIcon sx={{ fontSize: 12 }} />
                            {conversaAtiva.participantes.length}{' '}
                            {conversaAtiva.participantes.length === 1 ? 'participante' : 'participantes'}
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: 10.5, color: '#757575', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Conversa direta
                        </Typography>
                    )}
                </Box>
                <IconButton size="small" onClick={e => setAnchorEl(e.currentTarget)}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    {conversaAtiva.tipo_conversa === 'grupo' && onEditarGrupo && (
                        <MenuItem
                            sx={{ fontSize: 13 }}
                            onClick={() => {
                                setAnchorEl(null);
                                onEditarGrupo();
                            }}
                        >
                            Editar grupo
                        </MenuItem>
                    )}
                    <MenuItem sx={{ fontSize: 13, color: 'error.main' }} onClick={handleSair}>
                        {conversaAtiva.tipo_conversa === 'grupo' ? 'Sair do grupo' : 'Arquivar conversa'}
                    </MenuItem>
                </Menu>

                {/* Lista flutuante de participantes do grupo */}
                <Popover
                    open={Boolean(anchorParticipantes)}
                    anchorEl={anchorParticipantes}
                    onClose={() => setAnchorParticipantes(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <Box sx={{ px: 1.5, py: 0.8, borderBottom: '1px solid #eceff1' }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 'bold', color: '#455a64' }}>
                            Participantes ({conversaAtiva.participantes.length})
                        </Typography>
                    </Box>
                    <List dense sx={{ py: 0.5, minWidth: 210, maxWidth: 280, maxHeight: 280, overflowY: 'auto' }}>
                        {conversaAtiva.participantes.map(p => (
                            <ListItem key={p.id_usuario} sx={{ py: 0.3 }}>
                                <ListItemAvatar sx={{ minWidth: 34 }}>
                                    <Avatar
                                        src={p.photo_usuario ? `${apiUrl}/imguploaded/${p.photo_usuario}` : ""}
                                        sx={{ width: 26, height: 26, fontSize: 11, bgcolor: corAvatar(p.id_usuario) }}
                                    >
                                        {iniciaisNome(p.nome_usuario)}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        Number(p.id_usuario) === Number(idUsuarioLogado)
                                            ? `${p.nome_usuario} (você)`
                                            : p.nome_usuario
                                    }
                                    primaryTypographyProps={{ fontSize: 12, noWrap: true }}
                                />
                                {p.papel_participante === 'admin' && (
                                    <Chip label="admin" size="small" sx={{ height: 16, fontSize: 9, ml: 0.5 }} />
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Popover>
            </Box>

            {/* Mensagens */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    px: 1.2,
                    py: 1,
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: 3 },
                    '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#9e9e9e' },
                }}
            >
                {temMais && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <Button size="small" onClick={carregarMaisMensagens} disabled={carregandoMensagens} sx={{ fontSize: 11 }}>
                            {carregandoMensagens ? <CircularProgress size={14} /> : 'Carregar mensagens anteriores'}
                        </Button>
                    </Box>
                )}

                {carregandoMensagens && mensagens.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={22} />
                    </Box>
                )}

                {!carregandoMensagens && mensagens.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography sx={{ fontSize: 12.5, color: '#9e9e9e' }}>
                            Nenhuma mensagem ainda. Diga olá!
                        </Typography>
                    </Box>
                )}

                {mensagens.map((m, i) => {
                    const anterior = i > 0 ? mensagens[i - 1] : null;
                    const propria = Number(m.id_usuario_remetente) === Number(idUsuarioLogado);
                    const novoDia = !anterior || chaveDia(anterior.created_at) !== chaveDia(m.created_at);
                    // agrupa mensagens seguidas do mesmo autor (só a primeira mostra nome/avatar)
                    const mostrarAutor = novoDia || !anterior || anterior.id_usuario_remetente !== m.id_usuario_remetente || anterior.tipo_mensagem === 'sistema';

                    return (
                        <Fragment key={m.id_chat_mensagem}>
                            {novoDia && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.2 }}>
                                    <Chip size="small" label={rotuloDia(m.created_at)} sx={{ bgcolor: '#cfd8dc', fontSize: 10.5, height: 20 }} />
                                </Box>
                            )}
                            <ChatMensagem
                                mensagem={m}
                                propria={propria}
                                mostrarAutor={mostrarAutor}
                                podeApagar={propria}
                                lidaPorTodos={propria && m.id_chat_mensagem <= lidaAte}
                                nomesMencionaveis={nomesMencionaveis}
                                meuNome={meuNome}
                                idUsuarioLogado={idUsuarioLogado}
                                onApagar={apagarMensagem}
                                onBaixarAnexo={baixarAnexo}
                            />
                        </Fragment>
                    );
                })}
                <div ref={fimRef} />
            </Box>

            <ChatInput
                enviando={enviando}
                participantes={participantesMencao}
                idUsuarioLogado={idUsuarioLogado}
                onEnviarTexto={enviarMensagem}
                onEnviarAnexos={enviarAnexos}
            />
        </Box>
    );
}
